package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.MonthlyFinancials
import br.com.vvrefeicoes.portal.domain.model.OverviewData
import br.com.vvrefeicoes.portal.domain.model.UnitMargin
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.OverviewRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteOverviewRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : OverviewRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): OverviewData {
        val path =
            buildString {
                append("/operational/overview?")
                when (selection.mode) {
                    PeriodMode.MONTH -> append("period=mensal&year=${selection.month.year}&month=${selection.month.month}")
                    PeriodMode.YEAR -> append("period=anual&year=${selection.year}")
                    PeriodMode.TOTAL -> append("period=total")
                    PeriodMode.CUSTOM -> append("period=personalizado&start=${selection.start.id}&end=${selection.end.id}")
                }
                unitId?.takeIf { it.isNotBlank() }?.let { append("&unitId=").append(it.encodeURLParameter()) }
                manager?.takeIf { it.isNotBlank() }?.let { append("&manager=").append(it.encodeURLParameter()) }
                director?.takeIf { it.isNotBlank() }?.let { append("&director=").append(it.encodeURLParameter()) }
            }
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<OverviewApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<OverviewDto>().toDomain()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class OverviewApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class OverviewDto(
    val kpis: OverviewKpisDto = OverviewKpisDto(),
    val evolution: List<OverviewMonthlyDto> = emptyList(),
    val unitMargins: List<OverviewMarginDto> = emptyList(),
    val negativeUnitMargins: List<OverviewMarginDto> = emptyList(),
)

@Serializable
private data class OverviewKpisDto(
    val revenueRealCents: Long = 0,
    val revenuePlannedCents: Long = 0,
    val expenseRealCents: Long = 0,
    val expensePlannedCents: Long = 0,
    val resultCents: Long = 0,
    val fopagCents: Long = 0,
    val headcount: Int = 0,
    val transportedCents: Long = 0,
    val fopagPercent: Double = 0.0,
    val revenuePerFopag: Double = 0.0,
)

@Serializable
private data class OverviewMonthlyDto(
    val period: OverviewMonthDto = OverviewMonthDto(),
    val revenueCents: Long = 0,
    val expenseCents: Long = 0,
)

@Serializable
private data class OverviewMonthDto(
    val year: Int = 0,
    val month: Int = 1,
    val label: String = "",
)

@Serializable
private data class OverviewMarginDto(
    val name: String = "",
    val marginPercent: Double = 0.0,
)

private fun OverviewDto.toDomain(): OverviewData =
    OverviewData(
        revenueRealCents = kpis.revenueRealCents,
        revenuePlannedCents = kpis.revenuePlannedCents,
        expenseRealCents = kpis.expenseRealCents,
        expensePlannedCents = kpis.expensePlannedCents,
        resultCents = kpis.resultCents,
        fopagCents = kpis.fopagCents,
        headcount = kpis.headcount,
        transportedCents = kpis.transportedCents,
        fopagPercent = kpis.fopagPercent,
        revenuePerFopag = kpis.revenuePerFopag,
        evolution = evolution.map { MonthlyFinancials(MonthPeriod(it.period.year, it.period.month), it.revenueCents, it.expenseCents) },
        unitMargins = unitMargins.map { UnitMargin(it.name, it.marginPercent) },
        negativeUnitMargins = negativeUnitMargins.map { UnitMargin(it.name, it.marginPercent) },
    )
